import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Cortex Button (DESIGN_LANGUAGE_SPEC §22). Variants map to the spec recipes:
// primary = indigo→purple gradient + colored shadow, secondary = ghost/glass,
// destructive = rose, success = emerald→teal gradient, ghost = pill.
// All variants share a 44px minimum touch target (min-h-11) and a visible focus ring.

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'destructive'
  | 'success'
  | 'ghost';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Shows a spinner, disables interaction, and sets aria-busy. */
  loading?: boolean | undefined;
  leftIcon?: ReactNode | undefined;
  rightIcon?: ReactNode | undefined;
  /** Stretch to the width of the parent. */
  fullWidth?: boolean | undefined;
  children?: ReactNode | undefined;
}

const BASE =
  'relative inline-flex min-h-11 items-center justify-center gap-2 rounded-xl font-medium ' +
  'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ' +
  'disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]';

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
};

function variantClasses(variant: ButtonVariant, isDark: boolean): string {
  switch (variant) {
    case 'primary':
      return (
        'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg ' +
        'shadow-indigo-500/25 hover:from-indigo-400 hover:to-purple-500'
      );
    case 'success':
      return (
        'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg ' +
        'shadow-emerald-500/25 hover:from-emerald-400 hover:to-teal-500'
      );
    case 'destructive':
      return (
        'border border-rose-500/20 bg-rose-500/10 text-rose-400 ' +
        'hover:bg-rose-500/20 hover:text-rose-300'
      );
    case 'secondary':
      return isDark
        ? 'border border-white/[0.08] bg-white/[0.06] text-white/70 hover:bg-white/[0.1] hover:text-white'
        : 'border border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900';
    case 'ghost':
      return isDark
        ? 'rounded-full border border-white/[0.06] bg-white/[0.04] text-white/60 hover:bg-white/[0.08] hover:text-white'
        : 'rounded-full border border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900';
    default:
      return '';
  }
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    disabled,
    className,
    children,
    type,
    ...rest
  },
  ref,
) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isDisabled = disabled || loading;

  const classes = [
    BASE,
    SIZE[size],
    variantClasses(variant, isDark),
    fullWidth ? 'w-full' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        leftIcon
      )}
      {children}
      {!loading && rightIcon}
    </button>
  );
});
